import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";

function formatErr(detail) {
  if (!detail) return "No pudimos crear tu cuenta. Reintenta.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [loading, setLoading] = useState(false);

  const checkoutIntent = searchParams.get("checkout");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email.trim().toLowerCase(), password, razonSocial.trim() || null);
      toast.success("Cuenta creada. Empecemos con el onboarding.");
      // Si veníamos del CTA "Desbloquear todo" con intención de pago, saltamos al panel para iniciar Webpay.
      if (checkoutIntent === "transbank") {
        navigate("/cuenta?checkout=1");
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Crear cuenta" description="Crea tu cuenta gratuita en Ley de Datos Chile y genera tus documentos legales de la Ley 21.719." path="/registro" noindex />
      <section className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2">
        {/* Left: form */}
        <div className="px-6 py-14 sm:py-16 lg:py-20 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-6">
              Crear cuenta · gratis
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
              Empieza a <em className="italic text-blue-700">cumplir</em> hoy.
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Al completar el onboarding generamos automáticamente <strong>3 documentos esenciales</strong> con tus datos. La descarga (PDF/Markdown) se activa con Pro.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">
                  Razón social <span className="text-slate-400 normal-case tracking-normal">(opcional)</span>
                </label>
                <input
                  data-testid="register-razon"
                  type="text"
                  value={razonSocial}
                  onChange={(e) => setRazonSocial(e.target.value)}
                  placeholder="Comercial Los Andes SpA"
                  className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Email</label>
                <input
                  data-testid="register-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@empresa.cl"
                  className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Contraseña</label>
                <input
                  data-testid="register-password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                />
                <p className="mt-2 text-xs text-slate-500">Mínimo 8 caracteres.</p>
              </div>

              <button
                data-testid="register-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 active:scale-[0.98] disabled:opacity-60 text-white font-semibold transition-all inline-flex items-center justify-center gap-2"
              >
                {loading ? "Creando cuenta…" : (<>Crear cuenta gratis <ArrowRight className="w-4 h-4" /></>)}
              </button>

              <p className="text-xs text-slate-500 leading-relaxed">
                Al registrarte aceptas nuestra{" "}
                <Link to="/politica-privacidad" className="underline hover:text-slate-700">política de privacidad</Link>.
                Tus datos se usan solo para entregarte el servicio.
              </p>
            </form>

            <p className="mt-7 text-sm text-slate-600">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="font-semibold text-blue-700 hover:text-blue-900">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>

        {/* Right: dark brand panel */}
        <div className="relative hidden lg:flex bg-ink-950 text-white overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col justify-center px-12 max-w-xl">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-8">
              <Sparkles className="w-3.5 h-3.5" />
              Lo que recibes
            </div>
            <h2 className="font-display text-4xl xl:text-5xl text-white leading-tight">
              Cumplimiento <em className="italic text-blue-300">operativo</em>, no decorativo.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-sm max-w-md">
              Tras el onboarding (~7 min) generamos automáticamente tus 3 documentos esenciales. Los ves listos en tu cuenta; descargas con Pro.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "3 escaneos gratuitos + reporte por email",
                "Onboarding único · 3 documentos auto-generados",
                "Política de Privacidad · Cláusula · Catálogo (RAT)",
                "Pro $14.990 CLP · pago único · acceso permanente",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-white/10 font-mono text-[10px] text-slate-500 uppercase tracking-widest">
              v4.0 · session encrypted · TLS 1.3
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
